const LAZY_OBSERVED_ATTR = 'data-lazy-observed';
const OBSERVER_OPTIONS: IntersectionObserverInit = {
  rootMargin: '0px 0px 200px 0px',
  threshold: 0.01
};

let lazyObserver: IntersectionObserver | null = null;
let lazyDomObserver: MutationObserver | null = null;
let lcpPreloaded = false;

function getBgImage(dataBg: string): string {
  const bgImages = dataBg.split(',').map(image => image.trim());
  return window.innerWidth < 991 ? bgImages[1] ?? bgImages[0] : bgImages[0];
}

function activateLazyImage(img: HTMLImageElement): void {
  const picture = img.closest('picture');

  if (picture) {
    picture.querySelectorAll<HTMLSourceElement>('source[data-srcset]').forEach(source => {
      source.srcset = source.dataset.srcset ?? '';
      source.removeAttribute('data-srcset');
    });
  }

  if (img.dataset.src) {
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.classList.remove('lazy-img');
    img.onload = () => {
      img.style.opacity = '1';
    };
  }
}

function activateLazyVideo(video: HTMLVideoElement): void {
  if (!video.dataset.src) return;

  video.querySelectorAll<HTMLSourceElement>('source[data-src]').forEach(source => {
    source.src = source.dataset.src ?? '';
    source.removeAttribute('data-src');
  });

  video.src = video.dataset.src;
  video.removeAttribute('data-src');
  video.classList.remove('lazy-video');
  video.load();
}

function activateLazyBackground(el: HTMLElement): void {
  if (!el.dataset.bg) return;

  el.style.backgroundImage = `url(${getBgImage(el.dataset.bg)})`;
  el.removeAttribute('data-bg');
}

function handleIntersectionEntry(entry: IntersectionObserverEntry, observer: IntersectionObserver): void {
  if (!entry.isIntersecting) return;

  const target = entry.target;

  if (target instanceof HTMLImageElement && target.classList.contains('lazy-img')) {
    activateLazyImage(target);
    observer.unobserve(target);
    target.removeAttribute(LAZY_OBSERVED_ATTR);
    return;
  }

  if (target instanceof HTMLVideoElement && target.classList.contains('lazy-video')) {
    activateLazyVideo(target);
    observer.unobserve(target);
    target.removeAttribute(LAZY_OBSERVED_ATTR);
    return;
  }

  if (target instanceof HTMLElement && target.dataset.bg) {
    activateLazyBackground(target);
    observer.unobserve(target);
    target.removeAttribute(LAZY_OBSERVED_ATTR);
  }
}

function getLazyObserver(): IntersectionObserver | null {
  if (!('IntersectionObserver' in window)) return null;

  if (!lazyObserver) {
    lazyObserver = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => handleIntersectionEntry(entry, obs));
    }, OBSERVER_OPTIONS);
  }

  return lazyObserver;
}

function isAlreadyObserved(el: HTMLElement): boolean {
  return el.getAttribute(LAZY_OBSERVED_ATTR) === '1';
}

function markObserved(el: HTMLElement): void {
  el.setAttribute(LAZY_OBSERVED_ATTR, '1');
}

/** Подключить один элемент к lazy-load (без повторного observe). */
export function observeLazyElement(el: HTMLElement): void {
  const observer = getLazyObserver();
  if (!observer) return;
  if (isAlreadyObserved(el)) return;

  markObserved(el);
  observer.observe(el);
}

function collectLazyTargets(root: ParentNode): HTMLElement[] {
  const targets = new Set<HTMLElement>();

  const addAll = (selector: string) => {
    if (root instanceof Element || root instanceof Document || root instanceof DocumentFragment) {
      root.querySelectorAll<HTMLElement>(selector).forEach(el => targets.add(el));
    }
  };

  addAll('img.lazy-img');
  addAll('video.lazy-video');
  addAll('[data-bg]');

  return [...targets].filter(el => {
    if (el instanceof HTMLImageElement) return Boolean(el.dataset.src) || el.closest('picture')?.querySelector('source[data-srcset]');
    if (el instanceof HTMLVideoElement) return Boolean(el.dataset.src);
    return Boolean(el.dataset.bg);
  });
}

function preloadLcpIfNeeded(root: ParentNode): void {
  if (lcpPreloaded) return;
  if (root !== document) return;

  lcpPreloaded = true;

  const lcpElement = document.querySelector<HTMLElement>('.lcp-element');
  if (!lcpElement) return;

  if (lcpElement.tagName === 'IMG') {
    const img = lcpElement as HTMLImageElement;
    const href = img.dataset.src || img.src;
    if (href) {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = href;
      document.head.appendChild(preloadLink);
    }

    if (lcpElement.getBoundingClientRect().top < window.innerHeight && img.dataset.src) {
      activateLazyImage(img);
    }
  } else if (lcpElement.tagName === 'VIDEO') {
    const video = lcpElement as HTMLVideoElement;
    const href = video.dataset.src || video.src;
    if (href) {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'video';
      preloadLink.href = href;
      document.head.appendChild(preloadLink);
    }
  }
}

function lazyLoadMediaFallback(root: ParentNode): void {
  collectLazyTargets(root).forEach(el => {
    if (el instanceof HTMLImageElement) activateLazyImage(el);
    else if (el instanceof HTMLVideoElement) activateLazyVideo(el);
    else activateLazyBackground(el);
  });

  if (root instanceof Element || root instanceof Document || root instanceof DocumentFragment) {
    root.querySelectorAll<HTMLSourceElement>('source[data-srcset]').forEach(source => {
      source.srcset = source.dataset.srcset ?? '';
      source.removeAttribute('data-srcset');
    });
  }
}

/**
 * Подключает lazy-load для элементов внутри root (по умолчанию — document).
 * Безопасно вызывать повторно после AJAX: уже наблюдаемые/обработанные пропускаются.
 */
export function lazyLoadMedia(root: ParentNode = document): void {
  preloadLcpIfNeeded(root);

  const observer = getLazyObserver();

  if (!observer) {
    lazyLoadMediaFallback(root);
    return;
  }

  collectLazyTargets(root).forEach(observeLazyElement);
}

function scanAddedNodeForLazy(node: Node): void {
  if (node instanceof HTMLElement) {
    lazyLoadMedia(node);
    return;
  }

  if (node instanceof DocumentFragment) {
    lazyLoadMedia(node);
  }
}

/**
 * Следит за DOM и подключает lazy-load для новых img / video / data-bg.
 */
export function observeLazyMedia(root: ParentNode = document.body): void {
  if (lazyDomObserver) return;

  const target =
    root instanceof Document ? root.body : root instanceof HTMLElement ? root : null;

  if (!target) return;

  getLazyObserver();

  lazyDomObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(scanAddedNodeForLazy);
    }
  });

  lazyDomObserver.observe(target, { childList: true, subtree: true });
}
